<template>
    <DataTable
        :header="$t('currentProductAvailability')"
        :fields="fields"
        :items="filteredItems"
        :loading="loading"
        :error="request.getError()"
        show-export-button
        class="mb-4"
    >
        <template v-for="campaign in campaigns" #[`cell(campaign-${campaign.id})`]="{value}">
            <div :key="campaign.id" class="text-center line-height-1">
                <div v-if="parentComponent.showAvailabilityTime" class="pre">{{ getStatusText(value.statuses) }}</div>
                <div v-else class="line-height-1 height-20 d-flex justify-content-center align-items-center">
                    <feather v-if="value?.available" type="check" class="text-success" :size="24" />
                    <feather v-else type="x" class="text-danger" :size="24" />
                </div>
            </div>
        </template>
    </DataTable>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';

    export default {
        name: 'CurrentProductsAvailability',
        components: {
            DataTable,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestCurrentProductsAvailability,
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                items: [],
                filteredItems: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loading() {
                return this.request.getLoading() || this.requestCampaigns.getLoading() || this.parentComponent.waitingLoading;
            },
            campaigns() {
                return this.requestCampaigns.getData() || [];
            },
            selectedCampaigns() {
                return this.parentComponent.campaigns || [];
            },
            selectedStoreCampaigns() {
                if (this.selectedCampaigns.length > 0) {
                    return this.campaigns.filter(campaign => this.selectedCampaigns.includes(campaign.id));
                }
                return structuredClone(this.campaigns);
            },
            data() {
                return this.request.getData() || {};
            },
            fields() {
                const staticFields = [
                    { key: 'category', label: this.$t('category'), sortable: true, exportable: true },
                    { key: 'subcategory1', label: this.$t('subcategory1'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory2', label: this.$t('subcategory2'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory3', label: this.$t('subcategory3'), sortable: true, exportable: true, show: false },
                    { key: 'subcategory4', label: this.$t('subcategory4'), sortable: true, exportable: true, show: false },
                    { key: 'productName', label: this.$t('product'), sortable: true, exportable: true },
                    { key: 'sku', label: this.$t('model'), sortable: true, exportable: true },
                    { key: 'ean', label: 'EAN', sortable: true, exportable: true },
                ];
                const dynamicFields = this.selectedStoreCampaigns.map(campaign => ({
                    key: `campaign-${campaign.id}`,
                    label: campaign.name,
                    sortable: true,
                    exportable: true,
                    exportFormatter: val => (val ? this.$t('yes') : this.$t('no')),
                }));
                return [...staticFields, ...dynamicFields];
            },
        },
        watch: {
            data(data) {
                this.initItems();
                this.initFilteredItems();
            },
            'parentComponent.availabilities': {
                handler: 'initFilteredItems',
            },
        },
        methods: {
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            getStatusText(statuses) {
                if (statuses && Array.isArray(statuses)) {
                    if (!statuses.length) {
                        return '---';
                    }
                    return statuses.map(item => this.parentComponent.availabilitiesMap[item] || item).join('\n');
                }
                return statuses;
            },
            initItems() {
                const items = [];
                Object.values(this.data).forEach(productObj => {
                    const product = productObj.product || {};
                    let category = '';
                    let subcategory1 = '';
                    let subcategory2 = '';
                    let subcategory3 = '';
                    let subcategory4 = '';
                    if (product.categories && product.categories.length > 0 && product.categories[0].length > 0) {
                        const categories = [...product.categories[0]].reverse();
                        category = categories[0]?.name || '';
                        subcategory1 = categories[1]?.name || '';
                        subcategory2 = categories[2]?.name || '';
                        subcategory3 = categories[3]?.name || '';
                        subcategory4 = categories[4]?.name || '';
                    }
                    const row = {
                        category,
                        subcategory1,
                        subcategory2,
                        subcategory3,
                        subcategory4,
                        productName: product.name || '',
                        sku: product.sku || '',
                        ean: product.ean || '',
                    };
                    this.selectedStoreCampaigns.forEach(campaign => {
                        const key = `campaign-${campaign.id}`;
                        const available = productObj.availabilities ? productObj.availabilities[campaign.id] : false;
                        const statuses = productObj.availabilityStatuses ? productObj.availabilityStatuses[campaign.id] || [] : [];
                        row[key] = { available: !!available, statuses };
                    });
                    items.push(row);
                });
                this.items = items;
            },
            initFilteredItems() {
                const filter = (this.parentComponent.availabilities || []).map(item => Number(item)).filter(item => !Number.isNaN(item));
                if (filter.length === 0) this.filteredItems = [...this.items];
                const filterSet = new Set(filter);
                this.filteredItems = this.items.filter(row =>
                    // sprawdzamy tylko kampanie widoczne w tabeli
                    this.selectedStoreCampaigns.some(campaign => {
                        const cell = row[`campaign-${campaign.id}`];
                        if (!cell || !Array.isArray(cell.statuses)) return false;
                        // rzutuj statusy w komórce na Number (na wszelki wypadek)
                        const statusesNumbers = cell.statuses.map(statusNumber => Number(statusNumber)).filter(statusNumber => !Number.isNaN(statusNumber));
                        // wariant ANY: jest jakiekolwiek przecięcie
                        return statusesNumbers.some(statusNumber => filterSet.has(statusNumber));
                    })
                );
            },
        },
        created() {
            this.fetchCampaigns();
        },
    };
</script>
